import React from 'react'
import { Button } from '../../ui/button'
import { Users, Calendar, Share2, BarChart } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useResponseCount } from '../../../hooks/use-response-count'
import { debug } from '../../../lib/debug'
import { toast } from 'sonner'
import { trackEvent, EventCategory } from '../../../lib/analytics'

interface SurveyCode {
  code: string
  organization_name: string | null
  created_at: string
  expires_at: string
  has_insights?: boolean
  total_responses?: number
}

interface SurveyListItemProps {
  survey: SurveyCode
  copiedCode: string | null
  onCopyCode: (code: string) => void
  onViewResponses: (survey: SurveyCode) => void
}

export function SurveyListItem({ 
  survey, 
  copiedCode, 
  onCopyCode, 
  onViewResponses 
}: SurveyListItemProps) {
  const { count: responseCount, isLoading } = useResponseCount(survey.code)

  debug('SURVEY_CREATOR', `Survey ${survey.code} has ${responseCount} responses`)

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(survey.code)
      trackEvent({
        action: 'click',
        category: EventCategory.Survey,
        label: 'copy_survey_code'
      })
      onCopyCode(survey.code)
      toast.success('Survey code copied to clipboard')
    } catch (error) {
      console.error('Error copying code:', error)
      toast.error('Failed to copy code')
    }
  }

  const handleCopyShareUrl = async () => {
    try {
      const shareUrl = `${window.location.origin}/survey/${survey.code}/respond`
      await navigator.clipboard.writeText(shareUrl)
      trackEvent({
        action: 'click',
        category: EventCategory.Survey,
        label: 'copy_share_url'
      })
      toast.success('Share URL copied to clipboard')
    } catch (error) {
      console.error('Error copying share URL:', error)
      toast.error('Failed to copy share URL')
    }
  }

  const handleViewResponses = () => {
    trackEvent({
      action: 'click',
      category: EventCategory.Survey,
      label: 'view_responses'
    })
    onViewResponses({ ...survey, total_responses: responseCount })
  }

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 rounded-lg border hover:border-primary/50 transition-colors gap-4">
      <div className="space-y-1 w-full sm:w-auto">
        <h3 className="font-medium">
          {survey.organization_name || 'Unnamed Survey'}
        </h3>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1" title="Total responses">
            <Users className="w-4 h-4 flex-shrink-0" />
            {isLoading ? (
              <span>Loading...</span>
            ) : (
              <span>{responseCount} responses</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4 flex-shrink-0" />
            Expires {new Date(survey.expires_at).toLocaleDateString()}
          </div>
          {survey.has_insights && (
            <div className="flex items-center gap-1 text-primary">
              <BarChart className="w-4 h-4 flex-shrink-0" />
              <span>Insights Available</span>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
        <button
          onClick={handleCopyCode}
          className={`text-sm font-mono px-2 py-1 rounded w-full sm:w-auto text-center sm:text-left transition-colors ${
            copiedCode === survey.code 
              ? 'bg-primary/10 text-primary' 
              : 'bg-muted hover:bg-muted/50'
          }`}
        >
          {survey.code}
        </button>
        <div className="flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-start">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCopyShareUrl}
            className="h-8 w-8"
            title="Copy share URL"
          >
            <Share2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleViewResponses}
            className="h-8 w-8"
            title="View responses"
          >
            <Users className="h-4 w-4" />
          </Button>
          {survey.has_insights && (
            <Button
              variant="ghost"
              size="icon"
              asChild
              className="h-8 w-8"
              title="View insights"
              onClick={() => {
                trackEvent({
                  action: 'click',
                  category: EventCategory.Survey,
                  label: 'view_insights'
                })
              }}
            >
              <Link to={`/survey/${survey.code}/insights`}>
                <BarChart className="h-4 w-4" />
              </Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}